import { useEffect, useState } from "react";
import { Star, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Public reviews block used by both the public /profile/:userId page and
 * the logged-in user's own /profile page. Delegates to
 * ``GET /reviews/user/{id}`` which returns ONLY reviews that have already
 * passed the double-blind reveal rule (counterpart submitted OR 14 days
 * elapsed). No locked/unrevealed reviews are exposed here.
 *
 * Props:
 *   userId (required) — the subject user whose received reviews to fetch
 *   displayName (optional) — used for the friendly empty-state copy
 *   testIdPrefix (default "public-profile") — kept for backwards-compat
 *     with existing QA selectors on the public profile page.
 */
export default function ProfileReviewsSection({ userId, displayName, testIdPrefix = "public-profile" }) {
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/reviews/user/${userId}`);
        if (alive) setReviews(data);
      } catch {
        /* silent — section falls back to null (not rendered) on failure */
      }
    })();
    return () => { alive = false; };
  }, [userId]);

  if (!reviews) return null;
  const stats = reviews.stats || {};
  const list = reviews.reviews || [];
  const hasReviews = (stats.count || 0) > 0;
  const label = displayName || "This user";

  return (
    <div className="mt-12" data-testid={`${testIdPrefix}-reviews-section`}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="font-serif text-2xl inline-flex items-center gap-2">
          <Star size={18} className="text-[hsl(var(--sb-clay))]" /> Reviews
        </h2>
        {hasReviews && (
          <div className="text-sm text-muted-foreground" data-testid={`${testIdPrefix}-reviews-summary`}>
            <span className="font-serif text-lg text-foreground">
              <Star size={14} className="inline mb-0.5 fill-[hsl(var(--sb-clay))] text-[hsl(var(--sb-clay))]" />{" "}
              {stats.average?.toFixed(2)}
            </span>{" "}
            · {stats.count} review{stats.count === 1 ? "" : "s"}
          </div>
        )}
      </div>
      {!hasReviews ? (
        <div
          className="mt-4 rounded-2xl border border-border bg-muted/20 px-5 py-8 text-center"
          data-testid={`${testIdPrefix}-reviews-empty`}
        >
          <p className="font-serif text-lg">{label} doesn&apos;t have public reviews yet.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Reviews from completed Last Bookmark beta reads will show up here.
          </p>
        </div>
      ) : (
        <ul className="mt-5 space-y-4">
          {list.map((r) => (
            <li
              key={r.id}
              className="rounded-2xl border border-border bg-card p-5"
              data-testid={`${testIdPrefix}-review-${r.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  {r.reviewer_avatar_url ? (
                    <img
                      src={r.reviewer_avatar_url}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover bg-muted shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[hsl(var(--sb-moss))] grid place-items-center text-[hsl(var(--sb-forest-2))] font-semibold shrink-0">
                      {(r.reviewer_name || "?")[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-medium truncate">{r.reviewer_name || "Reviewer"}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {r.verified && (
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full border-2 border-[hsl(var(--sb-forest))] bg-[hsl(var(--sb-forest))] text-[hsl(var(--sb-cream))] px-3 py-1 text-xs font-bold shadow-sm"
                      title={r.job_title ? `From: ${r.job_title}` : "From a completed Last Bookmark beta read"}
                      data-testid={`${testIdPrefix}-review-verified-${r.id}`}
                    >
                      <ShieldCheck size={13} strokeWidth={2.5} /> Verified Beta Read
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 font-serif text-xl">
                    <Star size={16} className="fill-[hsl(var(--sb-clay))] text-[hsl(var(--sb-clay))]" />
                    {r.average?.toFixed(2)}
                  </span>
                </div>
              </div>
              {r.body && (
                <p className="mt-3 text-sm leading-relaxed whitespace-pre-line">{r.body}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
